import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Drawer,
  useTheme,
  Badge,
  Chip,
  Button,
  IconButton,
  Toolbar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  QrCode as QrCodeIcon,
  CalendarToday as CalendarTodayIcon,
  EventNote as EventNoteIcon,
  Storefront as StorefrontIcon,
  VpnKey as VpnKeyIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  BusinessCenter as BusinessCenterIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Exact colors from Figma design
const FIGMA_COLORS = {
  background: '#FFFFFF',
  primary: '#2563EB', // Blue-600
  primaryHover: '#1D4ED8', // Blue-700
  text: {
    primary: '#111827', // Gray-900
    secondary: '#6B7280', // Gray-500
    muted: '#9CA3AF', // Gray-400
  },
  border: '#E5E7EB', // Gray-200
  accent: '#F3F4F6', // Gray-50
  activeBackground: '#EFF6FF', // Blue-50
  activeBorder: '#DBEAFE', // Blue-100
};

// Styled Drawer matching Figma specifications
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 280,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 280,
    boxSizing: 'border-box',
    backgroundColor: FIGMA_COLORS.background,
    border: 'none',
    borderRight: `1px solid ${FIGMA_COLORS.border}`,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    zIndex: theme.zIndex.drawer,
  },
}));

// Logo container with exact Figma styling
const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '24px 20px',
  borderBottom: `1px solid ${FIGMA_COLORS.border}`,
}));

// Navigation section header
const NavSectionHeader = styled(Typography)(() => ({
  fontSize: '11px',
  fontWeight: 600,
  color: FIGMA_COLORS.text.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '20px 20px 8px 20px',
  margin: 0,
}));

// Navigation item styling matching Figma
const NavListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  margin: '2px 12px',
  borderRadius: '8px',
  padding: '10px 12px',
  minHeight: '44px',
  backgroundColor: isActive ? FIGMA_COLORS.activeBackground : 'transparent',
  border: isActive ? `1px solid ${FIGMA_COLORS.activeBorder}` : '1px solid transparent',
  '&:hover': {
    backgroundColor: isActive ? FIGMA_COLORS.activeBackground : FIGMA_COLORS.accent,
  },
  '& .MuiListItemIcon-root': {
    minWidth: '24px',
    marginRight: '12px',
    color: isActive ? FIGMA_COLORS.primary : FIGMA_COLORS.text.secondary,
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: isActive ? 600 : 500,
    color: isActive ? FIGMA_COLORS.primary : FIGMA_COLORS.text.primary,
  },
}));

// User profile section at bottom
const UserProfileSection = styled(Box)(() => ({
  marginTop: 'auto',
  padding: '20px',
  borderTop: `1px solid ${FIGMA_COLORS.border}`,
}));

// User card styling
const UserCard = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: FIGMA_COLORS.accent,
  marginBottom: '12px',
}));

interface FigmaSidebarProps {
  open: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
  userData?: {
    name?: string;
    email?: string;
    role?: string;
  };
  currentUser?: any;
  isMobile: boolean;
}

const FigmaSidebar: React.FC<FigmaSidebarProps> = ({
  open,
  onClose,
  currentView,
  onNavigate,
  toggleColorMode,
  mode,
  userData,
  currentUser,
  isMobile,
}) => {
  const theme = useTheme();

  // Navigation items matching the actual Dashboard routes
  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <HomeIcon />,
      view: 'dashboard'
    },
    {
      id: 'qrid',
      label: 'QR Records',
      icon: <QrCodeIcon />,
      view: 'qrid'
    },
    {
      id: 'storeactions',
      label: 'Store Actions',
      icon: <StorefrontIcon />,
      view: 'storeactions',
      badge: 3
    },
    {
      id: 'emhubaccess',
      label: 'Access Passes',
      icon: <VpnKeyIcon />,
      view: 'emhubaccess'
    },
    {
      id: 'bookings',
      label: 'Booking Dashboard',
      icon: <CalendarTodayIcon />,
      view: 'bookings'
    },
    {
      id: 'booking-settings',
      label: 'Booking Settings',
      icon: <EventNoteIcon />,
      view: 'booking-settings'
    },
    { 
      id: 'todo',
      label: 'To-Do Tasks',
      icon: <AssignmentIcon />,
      view: 'todo'
    },
    {
      id: 'seo',
      label: 'SEO Monitor',
      icon: <AssessmentIcon />,
      view: 'seo'
    },
    ...(userData?.role === 'admin' ? [{
      id: 'admin',
      label: 'User Management',
      icon: <PeopleIcon />,
      view: 'admin'
    }] : []),
  ];

  const settingsItems = [
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      view: 'settings'
    }
  ];

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar spacer for app bar */}
      <Toolbar />

      {/* Logo Section */}
      <LogoContainer>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${FIGMA_COLORS.primary} 0%, ${FIGMA_COLORS.primaryHover} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
          }}
        >
          <BusinessCenterIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: FIGMA_COLORS.text.primary,
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            Admin Portal
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '12px',
              color: FIGMA_COLORS.text.secondary,
              fontWeight: 500,
            }}
          >
            Management System
          </Typography>
        </Box>
      </LogoContainer>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <NavSectionHeader>Navigation</NavSectionHeader>
        <List sx={{ padding: 0 }}>
          {navigationItems.map((item) => (
            <NavListItem
              key={item.id}
              isActive={currentView === item.view}
              onClick={() => onNavigate(item.view)}
            >
              <ListItemIcon>
                {item.badge ? (
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '10px',
                        height: '18px',
                        minWidth: '18px',
                      }
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </NavListItem>
          ))}
        </List>

        <NavSectionHeader sx={{ marginTop: '16px' }}>Settings</NavSectionHeader>
        <List sx={{ padding: 0 }}>
          {settingsItems.map((item) => (
            <NavListItem
              key={item.id}
              isActive={currentView === item.view}
              onClick={() => onNavigate(item.view)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </NavListItem>
          ))}
        </List>
      </Box>

      {/* User Profile Section */}
      <UserProfileSection>
        <UserCard>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: FIGMA_COLORS.primary,
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {userData?.name?.charAt(0)?.toUpperCase() ||
             currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: FIGMA_COLORS.text.primary,
                lineHeight: 1.2,
                marginBottom: '2px',
              }}
            >
              {userData?.name || 'User'}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: FIGMA_COLORS.text.secondary,
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userData?.email || currentUser?.email || 'user@example.com'}
            </Typography>
            <Chip
              label={userData?.role === 'admin' ? 'Administrator' : 'User'}
              size="small"
              sx={{
                height: '20px',
                fontSize: '10px',
                fontWeight: 600,
                backgroundColor: userData?.role === 'admin' ? FIGMA_COLORS.primary : FIGMA_COLORS.text.secondary,
                color: 'white',
                '& .MuiChip-label': {
                  padding: '0 6px',
                },
              }}
            />
          </Box>
        </UserCard>

        {/* Bottom Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            startIcon={<LogoutIcon />}
            sx={{
              color: FIGMA_COLORS.text.secondary,
              fontSize: '12px',
              fontWeight: 500,
              textTransform: 'none',
              padding: '6px 8px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: FIGMA_COLORS.accent,
                color: FIGMA_COLORS.text.primary,
              },
            }}
          >
            Logout
          </Button>
          <IconButton
            onClick={toggleColorMode}
            size="small"
            sx={{
              color: FIGMA_COLORS.text.secondary,
              '&:hover': {
                backgroundColor: FIGMA_COLORS.accent,
                color: FIGMA_COLORS.text.primary,
              },
            }}
          >
            {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </IconButton>
        </Box>
      </UserProfileSection>
    </Box>
  );

  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawerContent}
    </StyledDrawer>
  );
};

export default FigmaSidebar;